import React from 'react';
import styled, { useTheme } from 'styled-components/macro';
import { SPACINGS } from '../../utils/style';
import { Pill, Radio } from '../../atoms';
import { IconWrapper, IconChevron } from '../../icons';

const FloatingListStyled = styled.div`
  display: block;
  padding: ${SPACINGS[4]};
  background-color: ${(p) => p.theme.colors.white};
  border: 1px solid ${(p) => p.theme.colors.primary};

  .header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .body {
    margin-top: ${SPACINGS[6]};
  }

  .list {
    display: flex;
    flex-direction: column;
    align-items: start;
    min-width: 200px;

    & > * {
      margin-bottom: ${SPACINGS[2]};
    }

    & > *:last-child {
      margin-bottom: 0;
    }
  }

  .footer {
    display: flex;
    justify-content: flex-end;
    margin-top: ${SPACINGS[6]};
  }
`;

type FloatingListBaseProps<Item> = {
  items: Item[];
  itemToString: (i: Item) => string;
  mainActionElement: React.ReactNode;
  secondaryActionElement?: React.ReactNode;
  isOpen?: boolean;
  style?: React.CSSProperties;
  testid?: string;
};

type FloatingListProps<Item> =
  | (FloatingListBaseProps<Item> & {
      selection: 'single';
      selected?: string;
      onChangeItem: (i: Item) => void;
    })
  | (FloatingListBaseProps<Item> & {
      selection: 'multiple';
      onRemoveItem: (i: Item) => void;
    });

function FloatingList<Item>(props: FloatingListProps<Item>) {
  const {
    items,
    itemToString,
    mainActionElement,
    secondaryActionElement,
    isOpen,
    style,
    testid,
  } = props;

  const theme = useTheme();

  const [isBodyVisible, setIsBodyVisible] = React.useState(isOpen ?? false);

  React.useEffect(() => {
    if (typeof isOpen !== 'undefined') setIsBodyVisible(isOpen);
  }, [isOpen]);

  React.useEffect(() => {
    if (items.length === 0) setIsBodyVisible(false);
  }, [items]);

  const selected =
    props.selection === 'single' && props.selected ? props.selected : '';
  const [selectedRadio, setSelectedRadio] = React.useState(selected);

  React.useEffect(() => {
    if (selected) setSelectedRadio(selected);
  }, [selected]);

  return (
    <FloatingListStyled style={style} data-testid={testid}>
      <div className="header">
        {mainActionElement}
        <IconWrapper
          disabled={items.length === 0}
          type="action"
          style={{ marginLeft: SPACINGS[4], minWidth: SPACINGS[8],
            minHeight: SPACINGS[8] }}
          hoverColor = { items.length ? theme.colors.primaryw : theme.colors.transparent }
          backgroundColor= { items.length ? theme.colors.primary95w : theme.colors.transparent }
          onClick={() => items.length > 0 && setIsBodyVisible((b) => !b)}
        > 
          <IconChevron
            stroke={theme.colors.primary}
            direction={isBodyVisible ? 'top' : 'bottom'}
          />
        </IconWrapper>
      </div>
      {isBodyVisible && (
        <div className="body">
          <div className="list">
            {props.selection === 'single'
              ? items.map((item, i) => (
                  <Radio
                    key={i}
                    id={i.toString()}
                    name={itemToString(item)}
                    value={itemToString(item)}
                    checked={selectedRadio === itemToString(item)}
                    onChange={() => {
                      setSelectedRadio(itemToString(item));
                      props.onChangeItem(item);
                    }}
                  >
                    {itemToString(item)}
                  </Radio>
                ))
              : items.map((item, i) => (
                  <Pill key={i} onRemove={() => props.onRemoveItem(item)}>
                    {itemToString(item)}
                  </Pill>
                ))}
          </div>
          <div className="footer">{secondaryActionElement}</div>
        </div>
      )}
    </FloatingListStyled>
  );
}

// typeof is because React.memo mess up with generic
export default React.memo(FloatingList) as typeof FloatingList;
